import type { LabCatalogueRow, LabCodedComment, LabParty, LabReportRow, LabResultRow } from './labTypes';

export function compareResultsDateTimeDesc(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  const at = a ? Date.parse(a) : 0;
  const bt = b ? Date.parse(b) : 0;
  const aMs = Number.isNaN(at) ? 0 : at;
  const bMs = Number.isNaN(bt) ? 0 : bt;
  return bMs - aMs;
}

export function formatLabDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('da-DK', {
    timeZone: 'Europe/Copenhagen',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function partyLines(party: LabParty | null | undefined): string[] {
  if (!party) return [];
  return [
    party.organisationName,
    party.departmentName,
    party.unitName,
    party.personInitials ? `Initialer: ${party.personInitials}` : null,
    party.identifier ? `ID: ${party.identifier}${party.identifierCode ? ` (${party.identifierCode})` : ''}` : null,
    party.ean ? `EAN: ${party.ean}` : null,
    party.fromLabIdentifier ? `Lab: ${party.fromLabIdentifier}` : null,
  ].filter((line): line is string => Boolean(line));
}

export function operatorSymbol(operator: string | null | undefined): string {
  switch (operator) {
    case 'stoerre_end':
    case 'Resultat_stoerre_el_mindre_end':
      return '>';
    case 'mindre_end':
      return '<';
    case 'lig_med':
    case 'ikke_angivet':
    case null:
    case undefined:
    case '':
      return '';
    default:
      return '';
  }
}

/** MIC / quantity cell: lig_med is shown as '=' (M 3.11). Biochemistry keeps operatorSymbol. */
export function quantityOperatorSymbol(operator: string | null | undefined): string {
  switch (operator) {
    case 'stoerre_end':
    case 'Resultat_stoerre_el_mindre_end':
      return '>';
    case 'mindre_end':
      return '<';
    case 'lig_med':
      return '=';
    case 'ikke_angivet':
    case null:
    case undefined:
    case '':
      return '';
    default:
      return '';
  }
}

export function displayQuantityValue(
  operator: string | null | undefined,
  value: string | null | undefined,
  unit: string | null | undefined,
): string {
  const raw = value?.trim() ?? '';
  if (!raw) return '';
  const op = quantityOperatorSymbol(operator);
  const unitPart = unit?.trim() ? ` ${unit.trim()}` : '';
  return op ? `${op} ${raw}${unitPart}` : `${raw}${unitPart}`;
}

export function displaySusceptibilityCell(row: {
  susceptibility?: string | null;
  susceptibility_value?: string | null;
  susceptibility_operator?: string | null;
  susceptibility_unit?: string | null;
}): string {
  const letter = row.susceptibility?.trim() ?? '';
  const qty = displayQuantityValue(
    row.susceptibility_operator,
    row.susceptibility_value,
    row.susceptibility_unit,
  );
  if (letter && qty) return `${letter} (${qty})`;
  return letter || qty || '—';
}

export type ValidationFlag = 'low' | 'high' | 'abnormal';

export type IntervalFields = Pick<
  LabResultRow,
  'reference_interval_type' | 'reference_lower' | 'reference_upper' | 'reference_interval_text'
>;

export function validationClass(code: string | null | undefined): ValidationFlag | null {
  switch (code) {
    case 'for_lav':
      return 'low';
    case 'for_hoej':
      return 'high';
    case 'unormal':
      return 'abnormal';
    default:
      return null;
  }
}

/** True when IntervalText and/or numeric limits are present (TypeOfInterval alone is not a range). */
export function hasReferenceInterval(row: IntervalFields): boolean {
  return Boolean(
    row.reference_interval_text?.trim() ||
      row.reference_lower?.trim() ||
      row.reference_upper?.trim(),
  );
}

/**
 * R 3.7 / M 3.14 arrows: ↓ for_lav and ↑ for_hoej when a range exists, ↕ for unormal,
 * and ↕ when ResultValidation is set without a reference range (protocol recommendation).
 */
export function validationArrow(
  validation: string | null | undefined,
  interval: IntervalFields | boolean,
): string {
  const flag = validationClass(validation);
  if (!flag) return '';
  const hasInterval = typeof interval === 'boolean' ? interval : hasReferenceInterval(interval);
  if (!hasInterval) return '↕';
  switch (flag) {
    case 'low':
      return '↓';
    case 'high':
      return '↑';
    case 'abnormal':
      return '↕';
    default: {
      const _never: never = flag;
      return _never;
    }
  }
}

const INTERVAL_TYPE_LABEL: Readonly<Record<string, string>> = {
  fysiologisk: 'Fysiologisk',
  terapeutisk: 'Terapeutisk',
  uspecificeret: 'Uspecificeret',
};

/** TypeOfInterval from XRPT01/XRPT05. Unknown codes are shown as received. */
export function intervalTypeLabel(type: string | null | undefined): string {
  const key = type?.trim() ?? '';
  if (!key) return '';
  return INTERVAL_TYPE_LABEL[key] ?? key;
}

export function statusLabel(code: string | null | undefined): string {
  switch (code) {
    case 'proeve_modtaget':
      return 'Prøve modtaget';
    case 'svar_midlertidigt':
      return 'Midlertidigt svar';
    case 'svar_endeligt':
      return 'Endeligt svar';
    case 'svar_rettet':
      return 'Rettet svar';
    case 'modtaget':
      return 'Modtaget';
    case 'del_svar':
      return 'Delsvar';
    case 'komplet_svar':
      return 'Komplet svar';
    default:
      return code || '—';
  }
}

/** XRPT05 3.26: only current ResultStatusCode=svar_midlertidigt is starred. */
export const PROVISIONAL_STAR = '*';
export const PROVISIONAL_STAR_LEGEND = '* = midlertidigt svar';

export function isProvisionalStarStatus(code: string | null | undefined): boolean {
  return code === 'svar_midlertidigt';
}

export function hasProvisionalStar(
  rows: ReadonlyArray<{ result_status_code?: string | null }>,
): boolean {
  return rows.some((row) => isProvisionalStarStatus(row.result_status_code));
}

function formatReferenceLimits(lower: string | null | undefined, upper: string | null | undefined): string {
  const lo = lower?.trim() ?? '';
  const hi = upper?.trim() ?? '';
  if (lo && hi) return `${lo}–${hi}`;
  if (lo) return `>${lo}`;
  if (hi) return `<${hi}`;
  return '';
}

/** IntervalText and lower/upper limits together (R 3.7 / M 3.14). TypeOfInterval is separate. */
export function intervalText(row: IntervalFields): string {
  const limits = formatReferenceLimits(row.reference_lower, row.reference_upper);
  const text = row.reference_interval_text?.trim() ?? '';
  if (limits && text) return `${limits} · ${text}`;
  return limits || text;
}

export function displayResultValue(row: Pick<LabResultRow, 'value' | 'operator' | 'result_status_code'>): string {
  if (row.result_status_code === 'proeve_modtaget') return '*****';
  const op = operatorSymbol(row.operator);
  const value = row.value ?? '';
  if (value.toUpperCase() === 'KOMM') return 'KOMM';
  return `${op}${value}`.trim();
}

export function displayVersionValue(
  row: Pick<LabResultRow, 'value' | 'operator' | 'unit' | 'result_status_code'>,
): string {
  const shown = displayResultValue(row) || '—';
  return row.unit ? `${shown} ${row.unit}` : shown;
}

/** ResultTextValue and ResultComments shown in full under the result value. */
export function displayResultLongText(
  row: Pick<LabResultRow, 'result_text_value' | 'result_comments'>,
): string | null {
  const parts = [row.result_text_value, row.result_comments].filter((s): s is string => Boolean(s));
  return parts.length > 0 ? parts.join('\n') : null;
}

export function isComplexMicro(summary: string | null | undefined, hasCultureOrMicroscopy: boolean): boolean {
  return hasCultureOrMicroscopy || Boolean(summary && summary.length > 40);
}

export function codedCommentMeta(item: LabCodedComment): string {
  return [item.code, item.codeType].filter(Boolean).join(' · ');
}

export function requisitionCommentItems(report: Pick<LabReportRow, 'requisition_comment_items' | 'requisition_comments'>): LabCodedComment[] {
  const items = report.requisition_comment_items;
  if (Array.isArray(items) && items.length > 0) return items;
  if (report.requisition_comments) return [{ text: report.requisition_comments }];
  return [];
}

export function laboratoryResultsComments(report: Pick<LabReportRow, 'laboratory_results_comments'>): LabCodedComment[] {
  return Array.isArray(report.laboratory_results_comments) ? report.laboratory_results_comments : [];
}

export function catalogueDisplayName(row: Pick<LabCatalogueRow, 'complete_name' | 'short_name'>): string {
  const complete = row.complete_name?.trim() ?? '';
  const short = row.short_name?.trim() ?? '';
  if (complete) return complete;
  if (short) return short;
  return '—';
}

export function catalogueSourceLabel(source: string): string {
  switch (source) {
    case 'npu':
      return 'NPU';
    case 'mds':
      return 'MDS';
    case 'local':
      return 'Lokal';
    case 'letter':
      return 'Brev';
    default:
      return source || '—';
  }
}
